import {
  RequestableState,
  createRequestableState,
  handleDone,
  handleFailed,
  handleStarting,
} from '@tock/shared/state/requestableState';
import { consumer, core, payloadToJSON, safeToNumber, toJSON, toJSONArray } from '@tock/coretypes';
import AppConstants from 'constants/AppConstants';
import _ from 'lodash';

type Modal = 'EDIT_PROFILE' | 'QUIZ';

interface State {
  numRequestsInProgress: number;
  achievements: core.PatronProfileAchievement[];
  allAchievements: consumer.AchievementDescription[];
  giftCardPurchaseHistory: RequestableState<core.TockGiftCardPurchaseHistory[]>;
  partnerPurchaseHistory: core.PastPurchase[] | null;
  purchaseHistory: core.PastPurchase[] | null;
  purchaseSummaries: consumer.PurchaseSummary[];
  transferHistory: core.PastPurchase[] | null;
  preferenceOptions: core.PreferenceOptions;
  quizQuestions: consumer.PatronQuizQuestion[] | null;
  quizResults: consumer.PatronQuizResult[];
  quizAnswers: consumer.PatronQuizAnswer[];
  wishlist: consumer.WishlistRequest[];
  isAchievementsInitialized: boolean;
  isAllAchievementsInitialized: boolean;
  isPurchaseSummariesInitialized: boolean;
  isPurchaseHistoryInitialized: boolean;
  isTransferHistoryInitialized: boolean;
  isPreferenceOptionsInitialized: boolean;
  isPartnerReservationsInitialized: boolean;
  isQuizResultsInitialized: boolean;
  openModal?: Modal;
  tockGiftCardBalance: RequestableState<core.TockGiftCardBalanceResponse>;
  purchasesAroundTime: {
    [dateTime: string]: core.PastPurchase[];
  };
  playlists: RequestableState<consumer.Playlist[]> & { numRequestsInProgress: number };
}

const initialState: State = {
  numRequestsInProgress: 0,
  achievements: [],
  allAchievements: [],
  giftCardPurchaseHistory: createRequestableState<core.TockGiftCardPurchaseHistory[]>(),
  partnerPurchaseHistory: null,
  purchaseHistory: null,
  purchaseSummaries: [],
  transferHistory: null,
  preferenceOptions: {
    cuisineOptions: [],
    dietaryRestrictionOptions: [],
    hospitalityOptions: [],
  },
  quizQuestions: [],
  quizResults: [],
  quizAnswers: [],
  wishlist: [],
  isAchievementsInitialized: false,
  isAllAchievementsInitialized: false,
  isPurchaseSummariesInitialized: false,
  isPurchaseHistoryInitialized: false,
  isTransferHistoryInitialized: false,
  isPreferenceOptionsInitialized: false,
  isPartnerReservationsInitialized: false,
  isQuizResultsInitialized: false,
  tockGiftCardBalance: createRequestableState<core.TockGiftCardBalanceResponse>(),
  purchasesAroundTime: {},
  playlists: { ...createRequestableState<consumer.Playlist[]>(), numRequestsInProgress: 0 },
};

export default function reducer(state = initialState, action: BaseTockAction): State {
  const { type, payload, context } = action;
  switch (type) {
    case AppConstants.PATRON_FETCH_PURCHASE_HISTORY_DONE: {
      return {
        ...state,
        purchaseHistory: payload,
        numRequestsInProgress: state.numRequestsInProgress - 1,
        isPurchaseHistoryInitialized: true,
      };
    }
    case AppConstants.PATRON_FETCH_PURCHASE_SUMMARIES_DONE: {
      return {
        ...state,
        purchaseSummaries: toJSONArray(payload),
        numRequestsInProgress: state.numRequestsInProgress - 1,
        isPurchaseSummariesInitialized: true,
      };
    }
    case AppConstants.PATRON_FETCH_LEGACY_TRANSFERS_DONE: {
      return {
        ...state,
        transferHistory: payload,
        numRequestsInProgress: state.numRequestsInProgress - 1,
        isTransferHistoryInitialized: true,
      };
    }
    case AppConstants.PARTNER_FETCH_ALL_RESERVATIONS_DONE: {
      return {
        ...state,
        partnerPurchaseHistory: payload,
        numRequestsInProgress: state.numRequestsInProgress - 1,
        isPartnerReservationsInitialized: true,
      };
    }
    case AppConstants.PATRON_FETCH_ACHIEVEMENTS_DONE: {
      return {
        ...state,
        achievements: payload,
        isAchievementsInitialized: true,
        numRequestsInProgress: state.numRequestsInProgress - 1,
      };
    }
    case AppConstants.PATRON_FETCH_QUIZ_QUESTIONS_DONE: {
      return {
        ...state,
        quizQuestions: toJSONArray(payload),
        numRequestsInProgress: state.numRequestsInProgress - 1,
      };
    }
    case AppConstants.PATRON_FETCH_QUIZ_RESULTS_DONE: {
      return {
        ...state,
        quizResults: toJSONArray(payload),
        isQuizResultsInitialized: true,
        numRequestsInProgress: state.numRequestsInProgress - 1,
      };
    }
    case AppConstants.PATRON_FETCH_QUIZ_ANSWERS_DONE: {
      return {
        ...state,
        quizAnswers: toJSONArray(payload),
        numRequestsInProgress: state.numRequestsInProgress - 1,
      };
    }
    case AppConstants.PATRON_FETCH_WISHLIST_DONE: {
      return {
        ...state,
        wishlist: toJSONArray(payload),
        numRequestsInProgress: state.numRequestsInProgress - 1,
      };
    }
    case AppConstants.ALL_PREFERENCE_OPTIONS_FETCH_DONE: {
      return {
        ...state,
        preferenceOptions: toJSON(payload),
        isPreferenceOptionsInitialized: true,
        numRequestsInProgress: state.numRequestsInProgress - 1,
      };
    }
    case AppConstants.FETCH_ALL_ACHIEVEMENTS_DONE: {
      return {
        ...state,
        allAchievements: payload,
        isAllAchievementsInitialized: true,
        numRequestsInProgress: state.numRequestsInProgress - 1,
      };
    }
    case AppConstants.PATRON_FETCH_PURCHASES_AROUND_TIME_DONE: {
      return {
        ...state,
        purchasesAroundTime: {
          ...state.purchasesAroundTime,
          [context.dateTime]: toJSONArray(payload),
        },
      };
    }
    case AppConstants.ALL_PREFERENCE_OPTIONS_FETCH_STARTING:
    case AppConstants.FETCH_ALL_ACHIEVEMENTS_STARTING:
    case AppConstants.PATRON_FETCH_ACHIEVEMENTS_STARTING:
    case AppConstants.PATRON_FETCH_LEGACY_TRANSFERS_STARTING:
    case AppConstants.PATRON_FETCH_PURCHASE_SUMMARIES_STARTING:
    case AppConstants.PATRON_FETCH_PURCHASE_HISTORY_STARTING:
    case AppConstants.PARTNER_FETCH_ALL_RESERVATIONS_STARTING:
    case AppConstants.PATRON_FETCH_QUIZ_QUESTIONS_STARTING:
    case AppConstants.PATRON_FETCH_QUIZ_RESULTS_STARTING:
    case AppConstants.PATRON_FETCH_QUIZ_ANSWERS_STARTING:
    case AppConstants.PATRON_FETCH_WISHLIST_STARTING: {
      return {
        ...state,
        numRequestsInProgress: state.numRequestsInProgress + 1,
      };
    }
    case AppConstants.ALL_PREFERENCE_OPTIONS_FETCH_FAILED:
    case AppConstants.FETCH_ALL_ACHIEVEMENTS_FAILED:
    case AppConstants.PATRON_FETCH_QUIZ_RESULTS_FAILED:
    case AppConstants.PATRON_FETCH_ACHIEVEMENTS_FAILED: {
      return {
        ...state,
        numRequestsInProgress: state.numRequestsInProgress - 1,
      };
    }
    case AppConstants.PATRON_FETCH_LEGACY_TRANSFERS_FAILED: {
      return {
        ...state,
        isPurchaseHistoryInitialized: false,
      };
    }
    case AppConstants.PATRON_FETCH_PURCHASE_SUMMARIES_FAILED: {
      return {
        ...state,
        numRequestsInProgress: state.numRequestsInProgress - 1,
        isPurchaseSummariesInitialized: false,
      };
    }
    case AppConstants.PATRON_FETCH_PURCHASE_HISTORY_FAILED: {
      return {
        ...state,
        numRequestsInProgress: state.numRequestsInProgress - 1,
        isTransferHistoryInitialized: false,
      };
    }
    case AppConstants.PATRON_FETCH_QUIZ_QUESTIONS_FAILED:
    case AppConstants.PATRON_FETCH_QUIZ_ANSWERS_FAILED:
    case AppConstants.PATRON_FETCH_WISHLIST_FAILED: {
      return {
        ...state,
        numRequestsInProgress: state.numRequestsInProgress - 1,
      };
    }
    case AppConstants.PARTNER_FETCH_ALL_RESERVATIONS_FAILED: {
      return {
        ...state,
        numRequestsInProgress: state.numRequestsInProgress - 1,
        isPartnerReservationsInitialized: false,
      };
    }
    case AppConstants.PATRON_OPEN_EDIT_PROFILE: {
      return {
        ...state,
        openModal: 'EDIT_PROFILE',
      };
    }
    case AppConstants.PATRON_CLOSE_EDIT_PROFILE: {
      return {
        ...state,
        openModal: undefined,
      };
    }
    // TODO: One action with modal as payload rather than two
    case AppConstants.PATRON_OPEN_QUIZ_MODAL: {
      return {
        ...state,
        openModal: 'QUIZ',
      };
    }
    case AppConstants.PATRON_CLOSE_QUIZ_MODAL: {
      return {
        ...state,
        openModal: undefined,
      };
    }

    // Listen to booking change events so we can avoid having to do a full
    // refresh to respond to them.
    case AppConstants.CANCEL_PURCHASE_DONE: {
      const cancelledPurchaseId = safeToNumber(payload.purchaseId);

      const partnerPurchaseHistory = state.partnerPurchaseHistory;
      const purchaseHistory = state.purchaseHistory;

      const cancelledPartnerPurchaseIndex = _.findIndex(partnerPurchaseHistory, (pastPurchase) => {
        return safeToNumber(pastPurchase.id) === cancelledPurchaseId;
      });

      const cancelledPurchaseIndex = _.findIndex(purchaseHistory, (pastPurchase) => {
        return safeToNumber(pastPurchase.id) === cancelledPurchaseId;
      });

      if (cancelledPartnerPurchaseIndex !== -1 && partnerPurchaseHistory) {
        partnerPurchaseHistory[cancelledPartnerPurchaseIndex].cancelledOrRefunded = true;
      }

      if (cancelledPurchaseIndex !== -1 && purchaseHistory) {
        purchaseHistory[cancelledPurchaseIndex].cancelledOrRefunded = true;
      }
      return {
        ...state,
        partnerPurchaseHistory,
        purchaseHistory,
      };
    }
    case AppConstants.TOCK_GIFT_CARD_BALANCE_STARTING: {
      return {
        ...state,
        tockGiftCardBalance: handleStarting(state.tockGiftCardBalance, action),
      };
    }
    case AppConstants.TOCK_GIFT_CARD_BALANCE_DONE: {
      return {
        ...state,
        tockGiftCardBalance: handleDone(state.tockGiftCardBalance, action),
      };
    }
    case AppConstants.TOCK_GIFT_CARD_REDEEM_DONE: {
      return {
        ...state,
        tockGiftCardBalance: handleDone(state.tockGiftCardBalance, action),
      };
    }
    case AppConstants.TOCK_GIFT_CARD_BALANCE_FAILED: {
      return {
        ...state,
        tockGiftCardBalance: handleFailed(state.tockGiftCardBalance, action),
      };
    }
    case AppConstants.TOCK_GIFT_CARD_PURCHASE_HISTORY_STARTING: {
      return {
        ...state,
        giftCardPurchaseHistory: handleStarting(state.giftCardPurchaseHistory, action),
        numRequestsInProgress: state.numRequestsInProgress + 1,
      };
    }
    case AppConstants.TOCK_GIFT_CARD_PURCHASE_HISTORY_DONE: {
      return {
        ...state,
        giftCardPurchaseHistory: handleDone(state.giftCardPurchaseHistory, action),
        numRequestsInProgress: state.numRequestsInProgress - 1,
      };
    }
    case AppConstants.TOCK_GIFT_CARD_PURCHASE_HISTORY_FAILED: {
      return {
        ...state,
        giftCardPurchaseHistory: handleFailed(state.giftCardPurchaseHistory, action),
        numRequestsInProgress: state.numRequestsInProgress - 1,
      };
    }

    case AppConstants.PATRON_PLAYLISTS_FETCH_STARTING: {
      return { ...state, playlists: { ...state.playlists, isRequesting: true } };
    }
    case AppConstants.PATRON_PLAYLISTS_FETCH_DONE: {
      return {
        ...state,
        playlists: {
          ...state.playlists,
          isInitialized: true,
          isRequesting: false,
          data: toJSONArray(payload),
        },
      };
    }
    case AppConstants.PATRON_PLAYLISTS_FETCH_FAILED: {
      return { ...state, playlists: { ...state.playlists, isRequesting: false } };
    }

    case AppConstants.PATRON_PLAYLIST_ADD_ITEM_DONE: {
      const playlist = payloadToJSON<consumer.Playlist>(payload);
      return {
        ...state,
        playlists: {
          ...state.playlists,
          data: state.playlists.data?.map((existingPlaylist) =>
            playlist.id === existingPlaylist.id ? playlist : existingPlaylist
          ),
        },
      };
    }

    case AppConstants.PATRON_PLAYLIST_UPSERT_STARTING: {
      return {
        ...state,
        playlists: {
          ...state.playlists,
          isRequesting: true,
          numRequestsInProgress: state.playlists.numRequestsInProgress + 1,
        },
      };
    }
    case AppConstants.PATRON_PLAYLIST_UPSERT_FAILED: {
      return {
        ...state,
        playlists: {
          isRequesting: false,
          numRequestsInProgress: Math.max(state.playlists.numRequestsInProgress - 1, 0),
        },
      };
    }
    case AppConstants.PATRON_PLAYLIST_UPSERT_DONE: {
      const playlist = payloadToJSON<consumer.Playlist>(payload);

      const playlistIndex = state.playlists.data?.findIndex(
        (existingPlaylist) => safeToNumber(playlist.id) === existingPlaylist.id
      );

      let updatedPlaylists;
      if (playlistIndex === -1) {
        updatedPlaylists = [...(state.playlists.data || []), playlist];
      } else {
        updatedPlaylists = state.playlists.data || [];
        updatedPlaylists[playlistIndex ?? -1] = playlist;
      }

      return {
        ...state,
        playlists: {
          ...state.playlists,
          data: updatedPlaylists.filter((playlist) => !playlist.isArchived),
          numRequestsInProgress: Math.max(state.playlists.numRequestsInProgress - 1, 0),
          isRequesting: false,
        },
      };
    }

    case AppConstants.PATRON_PLAYLIST_ITEM_DELETE_DONE: {
      const { playlistId, playlistItemId } = context;

      return {
        ...state,
        playlists: {
          ...state.playlists,
          data: state.playlists.data?.map((playlist) =>
            playlist.id === playlistId
              ? { ...playlist, items: playlist.items.filter((item) => item.id !== playlistItemId) }
              : playlist
          ),
        },
      };
    }

    default:
      return state;
  }
}
